import {
    SET_TAB_INDEX,
    SET_CURRENT_POLYGON_IDS,
    SET_CURRENT_POLYGON_ID,
    SET_POLYGONS_AND_TITLES,
    FETCH_POLYGONS_REQUEST,
    FETCH_POLYGONS_SUCCESS,
    FETCH_POLYGONS_FAILURE,
    FETCH_INTERSECTIONS_REQUEST,
    FETCH_INTERSECTIONS_SUCCESS,
    FETCH_INTERSECTIONS_FAILURE,
    FETCH_NEARBY_REQUEST,
    FETCH_NEARBY_SUCCESS,
    FETCH_NEARBY_FAILURE,
    FETCH_PLANNING_REQUEST,
    FETCH_PLANNING_SUCCESS,
    FETCH_PLANNING_FAILURE,
    FETCH_VALUE_REQUEST,
    FETCH_VALUE_SUCCESS,
    FETCH_VALUE_FAILURE,
    FETCH_AREA_INFO_REQUEST,
    FETCH_AREA_INFO_SUCCESS,
    FETCH_AREA_INFO_FAILURE
} from './Types';

import {
    IParcelAction,
    IParcelReducerState,
    Polygon,
    PolygonOnlyTitle,
    Title
} from './parcelTypes';

const defaultState = {
    tabIndex: 0,
    currentPolygonId: null,
    currentPolygonIds: [],
    polygons: {},
    titles: {}
};

function setIsFetchingForPolygonIds(
    state: IParcelReducerState,
    polygonIds: any[],
    fetchProperty: string,
    isFetching: boolean
) {
    return Object.assign(
        { ...state.polygons },
        ...polygonIds.map((polygonId: number) => ({
            [polygonId]: {
                ...state.polygons[polygonId],
                [fetchProperty]: isFetching
            }
        }))
    );
}

export default function reducer(state: IParcelReducerState = defaultState, action: IParcelAction) {
    switch (action.type) {
        case SET_TAB_INDEX:
            return {
                ...state,
                tabIndex: action.index
            };
        case SET_CURRENT_POLYGON_IDS:
            return {
                ...state,
                currentPolygonIds: action.polygonIds,
                currentPolygonId: action.polygonIds.length === 0 ? null : action.polygonIds[0],
                tabIndex: 0
            };
        case SET_CURRENT_POLYGON_ID:
            return {
                ...state,
                currentPolygonId: action.polygonId
            };
        case SET_POLYGONS_AND_TITLES:
            return {
                ...state,
                polygons: Object.assign(
                    { ...state.polygons },
                    ...action.polygons.map((polygon: PolygonOnlyTitle) => ({
                        [polygon.polygonId]: {
                            isLite: true,
                            ...state.polygons[polygon.polygonId],
                            ...polygon
                        }
                    }))
                )
            };
        case FETCH_POLYGONS_REQUEST:
            return {
                ...state,
                polygons: setIsFetchingForPolygonIds(
                    state,
                    action.polygonIds,
                    'isFetchingPolygon',
                    true
                )
            };
        case FETCH_POLYGONS_SUCCESS:
            return {
                ...state,
                polygons: Object.assign(
                    { ...state.polygons },
                    ...action.bundle.polygons.map((polygon: Polygon) => ({
                        [polygon.polygonId]: {
                            ...state.polygons[polygon.polygonId],
                            ...polygon,
                            isFetchingPolygon: false,
                            isLite: false
                        }
                    }))
                ),
                titles: Object.assign(
                    { ...state.titles },
                    ...action.bundle.titles.map((title: Title) => ({
                        [title.titleNo]: {
                            ...state.titles[title.titleNo],
                            ...title,
                            isFetchingTitle: false,
                            isLite: false
                        }
                    }))
                )
            };
        case FETCH_POLYGONS_FAILURE:
            return {
                ...state,
                polygons: setIsFetchingForPolygonIds(
                    state,
                    action.polygonIds,
                    'isFetchingPolygon',
                    false
                ),
                error: action.error
            };
        case FETCH_INTERSECTIONS_REQUEST:
            return {
                ...state,
                polygons: setIsFetchingForPolygonIds(
                    state,
                    [action.polygonId],
                    'isFetchingIntersections',
                    true
                )
            };
        case FETCH_INTERSECTIONS_SUCCESS:
            return {
                ...state,
                polygons: {
                    ...state.polygons,
                    [action.polygonId]: {
                        ...state.polygons[action.polygonId],
                        intersections: action.intersections,
                        isFetchingIntersections: false
                    }
                }
            };
        case FETCH_INTERSECTIONS_FAILURE:
            return {
                ...state,
                polygons: setIsFetchingForPolygonIds(
                    state,
                    [action.polygonId],
                    'isFetchingIntersections',
                    false
                ),
                error: action.error
            };
        case FETCH_NEARBY_REQUEST:
            return {
                ...state,
                polygons: setIsFetchingForPolygonIds(
                    state,
                    [action.polygonId],
                    'isFetchingNearby',
                    true
                )
            };
        case FETCH_NEARBY_SUCCESS:
            return {
                ...state,
                polygons: {
                    ...state.polygons,
                    [action.polygonId]: {
                        ...state.polygons[action.polygonId],
                        nearby: action.nearby,
                        isFetchingNearby: false
                    }
                }
            };
        case FETCH_NEARBY_FAILURE:
            return {
                ...state,
                polygons: setIsFetchingForPolygonIds(
                    state,
                    [action.polygonId],
                    'isFetchingNearby',
                    false
                ),
                error: action.error
            };
        case FETCH_PLANNING_REQUEST:
            return {
                ...state,
                polygons: setIsFetchingForPolygonIds(
                    state,
                    [action.polygonId],
                    'isFetchingPlanning',
                    true
                )
            };
        case FETCH_PLANNING_SUCCESS:
            return {
                ...state,
                polygons: {
                    ...state.polygons,
                    [action.polygonId]: {
                        ...state.polygons[action.polygonId],
                        planning: action.planning,
                        isFetchingPlanning: false
                    }
                }
            };
        case FETCH_PLANNING_FAILURE:
            return {
                ...state,
                polygons: setIsFetchingForPolygonIds(
                    state,
                    [action.polygonId],
                    'isFetchingPlanning',
                    false
                ),
                error: action.error
            };
        case FETCH_VALUE_REQUEST:
            return {
                ...state,
                polygons: setIsFetchingForPolygonIds(
                    state,
                    [action.polygonId],
                    'isFetchingValue',
                    true
                )
            };
        case FETCH_VALUE_SUCCESS:
            const titleNo = state.polygons[action.polygonId].titleNo;
            return {
                ...state,
                polygons: {
                    ...state.polygons,
                    [action.polygonId]: {
                        ...state.polygons[action.polygonId],
                        value: action.value.salesLandValues,
                        landUseValues: action.value.landUseValues,
                        isFetchingValue: false
                    }
                },
                titles: {
                    ...state.titles,
                    [titleNo]: {
                        ...state.titles[titleNo],
                        pricePaid: action.value.pricePaid,
                        isFetchingValue: false
                    }
                }
            };
        case FETCH_VALUE_FAILURE:
            return {
                ...state,
                polygons: setIsFetchingForPolygonIds(
                    state,
                    [action.polygonId],
                    'isFetchingValue',
                    false
                ),
                error: action.error
            };
        case FETCH_AREA_INFO_REQUEST:
            return {
                ...state,
                polygons: setIsFetchingForPolygonIds(
                    state,
                    [action.polygonId],
                    'isFetchingAreaInfo',
                    true
                )
            };
        case FETCH_AREA_INFO_SUCCESS:
            return {
                ...state,
                polygons: {
                    ...state.polygons,
                    [action.polygonId]: {
                        ...state.polygons[action.polygonId],
                        areaInfo: action.areaInfo,
                        isFetchingAreaInfo: false
                    }
                }
            };
        case FETCH_AREA_INFO_FAILURE:
            return {
                ...state,
                polygons: setIsFetchingForPolygonIds(
                    state,
                    [action.polygonId],
                    'isFetchingAreaInfo',
                    false
                ),
                error: action.error
            };
        default:
            return state;
    }
}
